import { httpClient } from '@/shell';
import type {
  AuctionEvent,
  AuctionEventCreateInput,
  AuctionEventUpdateInput,
  BrandingPresignedSlot,
  CampaignCreateResult,
} from '@neon-one/auctions-types';

// be-011 admin auction CRUD + publish endpoints per api-contracts §4.
// Each method returns the post-mutation AuctionEvent so the wizard
// store can hydrate the cached draft without a separate GET.
export const configureApi = {
  async create(input: AuctionEventCreateInput): Promise<AuctionEvent> {
    const resp = await httpClient().post<AuctionEvent>('/admin/auctions', input);
    return resp.data;
  },

  async update(uuid: string, input: AuctionEventUpdateInput): Promise<AuctionEvent> {
    const resp = await httpClient().patch<AuctionEvent>(
      `/admin/auctions/${encodeURIComponent(uuid)}`,
      input,
    );
    return resp.data;
  },

  async publish(uuid: string): Promise<AuctionEvent> {
    const resp = await httpClient().post<AuctionEvent>(
      `/admin/auctions/${encodeURIComponent(uuid)}/publish`,
    );
    return resp.data;
  },

  async unpublish(uuid: string): Promise<AuctionEvent> {
    const resp = await httpClient().post<AuctionEvent>(
      `/admin/auctions/${encodeURIComponent(uuid)}/unpublish`,
    );
    return resp.data;
  },

  // The inline `{ uploadUrl, cdnUrl }` this used to declare silently dropped key,
  // expiresAt and — once GT1-431 landed — requiredHeaders, which the browser MUST
  // forward on the presigned PUT. Use the shared wire type so the field can never
  // be lost to a hand-written subset again.
  async uploadBrandingImage(
    uuid: string,
    slot: 'logo' | 'banner',
    filename: string,
    contentType: string,
  ): Promise<BrandingPresignedSlot> {
    const resp = await httpClient().post<BrandingPresignedSlot>(
      `/admin/auctions/${encodeURIComponent(uuid)}/branding/upload`,
      { slot, filename, contentType },
    );
    return resp.data;
  },

  async getCampaigns(): Promise<{ campaigns: Array<{ id: string; name: string }>; error?: string }> {
    const resp = await httpClient().get<{ campaigns: Array<{ id: string; name: string }>; error?: string }>(
      '/admin/campaigns',
    );
    return resp.data;
  },

  async createCampaign(name: string): Promise<CampaignCreateResult> {
    const resp = await httpClient().post<CampaignCreateResult>(
      '/admin/campaigns',
      { name },
    );
    return resp.data;
  },
};
